import { useEffect, useRef, useState } from "react";
import { Headphones, Pause, Play, SkipForward, X, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, API } from "@/lib/api";
import { useBibleVersion } from "@/lib/bible";

type Props = { title: string; episodeIds: string[]; load: (id: string) => Promise<any>; kids?: boolean; testId?: string };

export function ListenAll({ title, episodeIds, load, kids, testId = "listen-all-button" }: Props) {
  const [open, setOpen] = useState(false);
  const [run, setRun] = useState(0);
  return (
    <>
      <button data-testid={testId} onClick={() => setOpen(true)} disabled={!episodeIds.length}
        className={`inline-flex items-center gap-2 rounded-full font-semibold text-white shadow-md transition-transform hover:-translate-y-0.5 ${kids ? "bg-[#E07A5F] px-5 py-2.5 text-lg" : "bg-[#1E293B] px-5 py-2.5 text-sm"}`}>
        <Headphones className="h-4 w-4" />Play the whole series
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`max-w-3xl overflow-hidden p-0 ${kids ? "font-kids" : ""}`} data-testid="listen-mode">
          <DialogTitle className="sr-only">Listening to {title}</DialogTitle>
          {open && <Player key={run} title={title} episodeIds={episodeIds} load={load} onClose={() => setOpen(false)} onRestart={() => setRun(run + 1)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Player({ title, episodeIds, load, onClose, onRestart }: Omit<Props, "kids" | "testId"> & { onClose: () => void; onRestart: () => void }) {
  const [ei, setEi] = useState(0);
  const [si, setSi] = useState(0);
  const [loaded, setLoaded] = useState<{ i: number; d: any } | null>(null);
  const [state, setState] = useState<"loading" | "playing" | "paused" | "done">("loading");
  const audio = useRef<HTMLAudioElement | null>(null);
  const amb = useRef<HTMLAudioElement | null>(null);
  const voice = localStorage.getItem("cast_voice") || "fable";
  const bv = useBibleVersion().id;
  const ambOn = localStorage.getItem("cast_ambience") !== "off";
  const ep = loaded && loaded.i === ei ? loaded.d : null;

  useEffect(() => { let live = true; load(episodeIds[ei]).then((d) => live && setLoaded({ i: ei, d })); return () => { live = false; }; }, [ei]); // eslint-disable-line
  const advance = () => {
    if (ep && si + 1 < ep.scenes.length) setSi(si + 1);
    else if (ei + 1 < episodeIds.length) { setSi(0); setEi(ei + 1); }
    else setState("done");
  };

  useEffect(() => {
    if (!ep || state === "done") return;
    let live = true;
    const scene = ep.scenes[si];
    setState("loading");
    const a = new Audio(`${API}/narration/${ep.id}/${si}?voice=${voice}&bv=${bv}`);
    audio.current = a;
    a.onplaying = () => live && setState("playing");
    a.onpause = () => live && !a.ended && setState("paused");
    a.onended = () => live && advance();
    a.onerror = () => live && advance();
    const src = ambOn && scene.ambience_url ? `${API}${scene.ambience_url}` : null;
    if (src && amb.current?.dataset.src !== src) { amb.current?.pause(); amb.current = Object.assign(new Audio(src), { loop: true, volume: 0.14 }); amb.current.dataset.src = src; }
    if (!src) { amb.current?.pause(); amb.current = null; }
    api.get("/auth/me").catch(() => {}).finally(() => { if (!live) return; a.play().catch(() => live && setState("paused")); amb.current?.play().catch(() => {}); });
    return () => { live = false; a.pause(); a.removeAttribute("src"); };
  }, [ep, si, state === "done"]); // eslint-disable-line
  useEffect(() => () => { audio.current?.pause(); amb.current?.pause(); }, []);
  useEffect(() => { if (state === "done") { audio.current?.pause(); amb.current?.pause(); } }, [state]);

  const toggle = () => {
    if (!audio.current) return;
    if (state === "playing") { audio.current.pause(); amb.current?.pause(); } else { audio.current.play(); amb.current?.play().catch(() => {}); }
  };
  const scene = ep?.scenes[si];
  const clip = ep?.clip_url && si === ep.clip_scene ? `${API}${ep.clip_url}` : null;
  return (
    <div className="bg-[#FAF7F2]">
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-slate-900">
        {scene && (clip ? <video key={clip} src={clip} poster={scene.image} autoPlay muted loop playsInline className="h-full w-full object-cover" data-testid="listen-clip" />
          : <img key={scene.image + si} src={scene.image} alt={scene.title} className="ken-burns h-full w-full object-cover" />)}
        <button onClick={onClose} data-testid="listen-close-button" aria-label="Close" className="absolute right-3 top-3 rounded-full bg-black/40 p-2 text-white backdrop-blur"><X className="h-4 w-4" /></button>
      </div>
      <div className="space-y-4 p-6">
        <p className="eyebrow" data-testid="listen-progress">{title} · Story {ei + 1} of {episodeIds.length}{ep ? ` · Scene ${si + 1} of ${ep.scenes.length}` : ""}</p>
        <h2 className="font-display text-3xl font-semibold text-slate-900" data-testid="listen-title">{state === "done" ? "The end of the series" : ep ? `${ep.title}: ${scene.title}` : "Loading…"}</h2>
        {state !== "done" && scene && <p className="line-clamp-3 text-slate-600">{scene.text}</p>}
        <div className="flex items-center gap-3">
          {state === "done" ? <button data-testid="listen-restart-button" onClick={onRestart} className="rounded-full bg-[#1E293B] px-5 py-2.5 text-sm font-semibold text-white">Listen again</button> : <>
            <button data-testid="listen-toggle-button" onClick={toggle} className="flex h-12 w-12 items-center justify-center rounded-full bg-[#C86D51] text-white shadow-md">
              {state === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : state === "playing" ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </button>
            <button data-testid="listen-skip-button" onClick={advance} className="flex items-center gap-1 rounded-full border border-[#E2DBCE] bg-white px-4 py-2 text-sm font-semibold text-slate-700"><SkipForward className="h-4 w-4" />Next</button>
          </>}
        </div>
      </div>
    </div>
  );
}
